import { type ImageBuilder } from '@lib/sanity/imageBuilder';
import type { VisualImageProjection } from '@lib/sanity/types/common';
import { SizesProp } from '@lib/types';
import { getImageDimensions } from '@sanity/asset-utils';
import cn from '@utils/cn';
import Image from 'next/image';
import type { CSSProperties } from 'react';
import { ImageDimensions } from 'sanity';
import placeholderImage from '../../../../public/images/blog_image_placeholder_.webp';

export type VisualsImageProps = {
  image?: VisualImageProjection | null;
  isEagerLoad?: boolean | null;
  /** Loads the image with the page without the preload and high priority `isEagerLoad` adds. */
  loading?: 'eager' | 'lazy';
  className?: string;
  sizes?: SizesProp;
  style?: CSSProperties;
  showPlaceholderImage?: boolean;
};

export function renderVisualsImage(builder: ImageBuilder, props: VisualsImageProps) {
  const { image, isEagerLoad, loading, className, style, sizes, showPlaceholderImage } = props;

  if (image?.image?.asset?.url) {
    return renderImage(builder, {
      image,
      sizes,
      isEagerLoad,
      loading,
      style,
      className,
    });
  }

  if (showPlaceholderImage) {
    return (
      <Image
        key="placeholder"
        src={placeholderImage}
        width={1500}
        height={860}
        alt="Placeholder Image"
        className={cn('select-none', className)}
        priority={Boolean(isEagerLoad)}
        fetchPriority={isEagerLoad ? 'high' : undefined}
        loading={loading}
        style={style}
      />
    );
  }
  return null;
}

type RenderImageProps = Omit<VisualsImageProps, 'imageSmall'>;

function renderImage(builder: ImageBuilder, props: RenderImageProps) {
  const { sizes, isEagerLoad, loading, style, className } = props;
  if (!props.image?.image?.asset?.url) {
    return null;
  }
  const {
    image: {
      asset: { url, originalFilename: filename },
    },
    alt: imageAlt,
    fit,
    quality,
  } = props.image;

  const key = `${url}:${filename ?? ''}:${stableThreeDigitKey(`${url}:${filename ?? ''}`)}`;

  const dimensions = props.image.image.asset.metadata?.dimensions ?? undefined;
  const { width, height } = calcImageProps(url, dimensions);

  const isSvg = isSvgImage(url, filename);
  if (isSvg) {
    return (
      <Image
        key={key}
        src={url}
        unoptimized
        width={width}
        height={height}
        sizes={renderSizes(sizes)}
        alt={imageAlt ?? 'Image'}
        className={cn('select-none', className)}
        priority={Boolean(isEagerLoad)}
        fetchPriority={isEagerLoad ? 'high' : undefined}
        loading={loading}
        style={style}
      />
    );
  }

  // Build a stable Sanity CDN URL in a Server Component-friendly way.
  // Avoid passing `loader` functions into `next/image` (Client Component boundary).
  const src = vanityFilename(
    builder
      .image(url)
      .width(width)
      .fit(fit ?? 'max')
      .format('webp')
      .quality(quality ?? 70)
      .url(),
    filename,
  );

  return (
    <Image
      key={key}
      src={src}
      quality={quality ?? 70}
      width={width}
      height={height}
      sizes={renderSizes(sizes)}
      alt={imageAlt ?? 'Image'}
      className={cn('select-none', className)}
      priority={Boolean(isEagerLoad)}
      fetchPriority={isEagerLoad ? 'high' : undefined}
      loading={loading}
      style={style}
      // eslint-disable-next-line @nx/workspace-no-hardcoded-strings
      placeholder="blur"
      blurDataURL={placeholderImage.src}
    />
  );
}

function calcImageProps(url: string, dimensions?: ImageDimensions) {
  // Prefer Sanity-provided dimensions when available; fall back to parsing from url
  const widthFromSanity = dimensions?.width ?? undefined;
  const heightFromSanity = dimensions?.height ?? undefined;
  const parsed = getImageDimensions(url);
  const width = widthFromSanity ?? parsed.width;
  const height = heightFromSanity ?? parsed.height;

  return {
    width,
    height,
  };
}

function vanityFilename(url: string, filename?: string) {
  if (!filename) {
    return url;
  }

  // add the filename into the path before query parameters
  const parts = url.split('?');
  const newSrc = `${parts[0].replace(/\/$/, '')}/${filename}`;

  return parts.length < 2 ? newSrc : `${newSrc}?${parts[1]}`;
}

function renderSizes(sizes?: SizesProp) {
  if (!sizes) {
    return undefined;
  }

  const parts: string[] = [];

  if (sizes.sm) {
    parts.push(`(max-width: 480px) ${sizes.sm}`);
  }
  if (sizes.md) {
    parts.push(`(max-width: 768px) ${sizes.md}`);
  }
  if (sizes.lg) {
    parts.push(`(max-width: 1024px) ${sizes.lg}`);
  }
  if (sizes.xl) {
    parts.push(`(max-width: 1280px) ${sizes.xl}`);
  }
  parts.push(sizes.full ?? '100vw');

  return parts.join(', ');
}

function isSvgImage(url: string, filename?: string) {
  if (filename?.toLowerCase().endsWith('.svg')) {
    return true;
  }

  const urlPath = url.split('?')[0]?.split('#')[0] ?? url;
  return urlPath.toLowerCase().endsWith('.svg');
}

function stableThreeDigitKey(seed: string) {
  // Generate a stable 3-digit number (100-999) from a string seed.
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 900) + 100;
}
